import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import Image from "@mui/icons-material/Image";
import TextSnippet from "@mui/icons-material/TextSnippet";
import Videocam from "@mui/icons-material/Videocam";
import { Box, Button, Popover } from "@mui/material";
import { type FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDialog } from "../../../../../../utils/hooks/use-dialog";
import { usePopover } from "../../../../../../utils/hooks/use-popover";
import { useCreateAssetCallback } from "../../../../../assets/hooks/use-upsert-asset";
import { URLInput } from "../image-button";
import { ToolbarIconButton } from "../toolbar-icon-button";
import { UploadFileEditorModal } from "./upload-file-editor-modal";

export const ContentLibraryModalAddNew: FC<{
  onClose: () => void;
  folders_feature_flag?: boolean;
}> = ({ onClose, folders_feature_flag }) => {
  const { t } = useTranslation();
  const uploadModal = useDialog<{ type: "pdf" | "image" | "video" }>();
  const createAsset = useCreateAssetCallback();

  const [open, setOpen] = useState<boolean>(false);
  const popover = usePopover<HTMLButtonElement | null>();

  const [urlAnchorEl, setUrlAnchorEl] = useState<Element | null>(null);
  const closeLinkUrl = useCallback(() => {
    setUrlAnchorEl(null);
    onClose();
  }, [onClose]);

  return (
    <>
      <Button
        color="primary"
        variant="contained"
        startIcon={<AddCircleOutline data-testid="AddCircleOutlineIcon" />}
        data-analytics-id="content-library-modal-upload"
        ref={popover.anchorRef}
        onClick={() => setOpen(!open)}
      >
        {t("Upload")}
      </Button>
      <Popover
        // eslint-disable-next-line react-hooks/refs
        anchorEl={popover.anchorRef.current}
        onClose={() => setOpen(false)}
        open={open}
        sx={{ minWidth: 100, padding: 5, maxWidth: 300 }}
      >
        <Box display="flex" flexDirection="column">
          <ToolbarIconButton
            data-analytics-id="content-library-modal-upload-image"
            disabled={false}
            label={t("Image")}
            icon={<Image fontSize="medium" />}
            data-testid="content-library-modal-add-new-upload-image"
            aria-label="add-image"
            onClick={() => uploadModal.handleOpen({ type: "image" })}
          />
          <ToolbarIconButton
            data-analytics-id="content-library-modal-upload-video"
            disabled={false}
            label={t("Video")}
            icon={<Videocam fontSize="medium" />}
            data-testid="content-library-modal-add-new-upload-video"
            aria-label="add-video"
            onClick={() => uploadModal.handleOpen({ type: "video" })}
          />
          <ToolbarIconButton
            data-analytics-id="content-library-modal-upload-file"
            disabled={false}
            label={t("File")}
            icon={<TextSnippet fontSize="medium" />}
            data-testid="content-library-modal-add-new-upload-file"
            aria-label="add-image"
            onClick={() => uploadModal.handleOpen({ type: "pdf" })}
          />
        </Box>
      </Popover>
      {uploadModal.open && uploadModal.data && (
        <UploadFileEditorModal
          type={uploadModal.data.type}
          heading={t("Upload")}
          onSave={createAsset}
          open={uploadModal.open}
          onOpen={uploadModal.handleOpen}
          onClose={() => {
            onClose();
            uploadModal.handleClose();
          }}
          onCancel={uploadModal.handleClose}
          folders_feature_flag={folders_feature_flag}
        />
      )}
      <Popover
        open={Boolean(urlAnchorEl)}
        anchorEl={urlAnchorEl}
        onClose={() => setUrlAnchorEl(null)}
      >
        <URLInput onClose={closeLinkUrl} />
      </Popover>
    </>
  );
};
